import { VscEye, VscEyeClosed } from "react-icons/vsc";
import { useCompleteRegistrationMutation, useSendRegistrationOtpMutation, useVerifyRegistrationOtpMutation } from "../../../redux/services/apiSlice";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ImageCarousel from "../../ImageCarousel/ImageCarousel";
import { IoClose } from "react-icons/io5";
import LoaderMini from "../../Loader/LoaderMini";
import { login } from "../../../redux/slices/authSlice";
import { mergeGuestCartToUserCart } from "../../../redux/slices/cartSlice";
import saudi_flag from "../../../assets/icons/saudi_flag.webp";
import { triggerGTMEvent } from "../../../utils/gtm";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

// import { FcGoogle } from "react-icons/fc";










const RegisterPopup = ({
  setShowRegisterForm,
  handlePopup,
  setShowLoginForm,
  showLoginForm,
}) => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const registerFormRef = useRef(null);
  const registerFormCloseButtonRef = useRef(null);
  const carouselRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
  const [sendOtp, { isLoading: sendingOtp }] = useSendRegistrationOtpMutation();
  const [verifyOtp, { isLoading: verifyingOtp }] = useVerifyRegistrationOtpMutation();
  const [completeRegistration, { isLoading: completingRegistration }] = useCompleteRegistrationMutation();

  const [currentStep, setCurrentStep] = useState(1);
  const [verificationToken, setVerificationToken] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sendOtpErr, setSendOtpErr] = useState("");
  const [verifyOtpErr, setVerifyOtpErr] = useState("");
  const [timeLeft, setTimeLeft] = useState(600);
  const timerRef = useRef(null);
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // Only accept digits
    const newOtp = [...otpDigits];
    newOtp[index] = value;
    setOtpDigits(newOtp);

    if (value && index < 3) {
      document.getElementById(`otp-${index + 1}`).focus();
    }

    if (value === "" && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }

    setOtpCode(newOtp.join(""));
  };

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch("password");

  const watchPhone = watch("phone");

  useEffect(() => {
    clearErrors("phone");
  }, [watchPhone, clearErrors]);

  const onSubmit = async (data) => {
    try {
      let formattedPhone = phoneNumber;
      if (!formattedPhone.startsWith("+966")) {
        formattedPhone = `+966${formattedPhone.replace(/^0+/, "")}`;
      }

      const response = await completeRegistration({
        ...data,
        firebaseToken: "mock_firebase_token",
        verification_token: verificationToken,
        phone: formattedPhone,
      }).unwrap();
      dispatch(login({ token: response.token, user: response.user }));
      triggerGTMEvent("sign_up", {
        user_id: response.user?.id,
        name: response.user?.name,
        email: response.user?.email,
        phone: response.user?.phone,
        method: "otp"
      });
      
      console.log("Merging cart now...");
      setTimeout(() => {
        dispatch(mergeGuestCartToUserCart());
      }, 0);
      navigate(from, { replace: true });
      setShowRegisterForm(false);
    } catch (error) {
      if (error?.data?.errors) {
        if (error?.data?.errors?.email) {
          setError("email", {
            type: "manual",
            message: t("emailAlreadyUsed"),
          });
        } else {
          setError("root", {
            type: "manual",
            message: t("registrationFailed")
          });
        }
      } else if (error?.data?.message) {
        setError("root", {
          type: "manual",
          message: t("otpExpired")
        });
      }
    }
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  const handleSendOtp = async () => {
    try {
      if (!/^\d{7,10}$/.test(phoneNumber)) {
        setSendOtpErr(t("phoneValidation"));
        return;
      }

      let formattedPhone = phoneNumber;
      if (!formattedPhone.startsWith("+966")) {
        formattedPhone = `+966${formattedPhone.replace(/^0+/, "")}`;
      }

      await sendOtp({ phone: formattedPhone }).unwrap();
      setOtpSent(true);
      setCurrentStep(2);
      setTimeLeft(600); // Restart timer
      if (timerRef.current) clearInterval(timerRef.current);
      startTimer();
      setSendOtpErr("");
    } catch (error) {
      if (error?.data?.message && error?.data?.message.includes("Phone number already")) {
        setSendOtpErr(t("alreadyUsedPhone"));
      } else {
        console.log("Failed to send OTP", error.data.message);
        setSendOtpErr(t("failedToSendOtp"));
      }
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleVerifyOtp = async () => {
    try {
      let formattedPhone = phoneNumber;
      if (!formattedPhone.startsWith("+966")) {
        formattedPhone = `+966${formattedPhone.replace(/^0+/, "")}`;
      }
      const response = await verifyOtp({
        phone: formattedPhone,
        otp: otpCode,
      }).unwrap();
      setVerificationToken(response.verification_token);
      setCurrentStep(3);
      setVerifyOtpErr("");
    } catch (error) {
      console.error("Invalid OTP", error);
      setVerifyOtpErr(t("invalidOtp"));
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        registerFormRef.current &&
        !registerFormRef.current.contains(e.target) &&
        registerFormCloseButtonRef.current &&
        !registerFormCloseButtonRef.current.contains(e.target) &&
        carouselRef.current &&
        !carouselRef.current.contains(e.target)
      ) {
        setShowRegisterForm(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setShowRegisterForm]);

  const handleCloseForm = () => {
    setShowRegisterForm(false);
    if (showLoginForm) {
      setShowLoginForm(false);
    }
  };

  const handleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleShowConfirmedPassword = () => {
    setShowConfirmedPassword((prev) => !prev);
  };

  return (
    <div
      className="fixed w-screen h-screen flex items-center justify-center 
    z-50 top-0 right-0 bg-black/15 backdrop-blur-sm"
    >
      <div className="flex flex-col items-center justify-center w-full h-full">
        <div
          className="flex items-center justify-center w-full lg:w-[90%] 
        min-h-[650px] rounded-2xl lg:bg-white px-2 relative"
        >
          <div className="w-1/2 hidden lg:flex" ref={carouselRef}>
            <ImageCarousel height="h-[650px]" width="w-full" />
          </div>
          <button
            type="button"
            ref={registerFormCloseButtonRef}
            onClick={handleCloseForm}
            className={`absolute hidden lg:flex top-4  text-gray-500
             hover:text-gray-700 ${isEng ? "right-4" : "left-4"}`}
          >
            <IoClose size={24} />
          </button>
          <div className="lg:w-1/2 w-full flex items-center justify-center">
            <div
              ref={registerFormRef}
              className="bg-white px-6 py-10 lg:py-5 rounded-lg  
               w-80 lg:w-[30rem] flex flex-col 
               items-start relative justify-start lg:justify-start  
               h-[95vh] overflow-y-scroll lg:overflow-auto"
            >
              <button
                ref={registerFormCloseButtonRef}
                className="sticky top-0 lg:absolute lg:top-10 lg:left-[30rem] 
                left-80 text-gray-500 hover:text-gray-700 flex lg:hidden"
                onClick={handleCloseForm}
              >
                <IoClose size={24} />
              </button>
              <h2 className="text-xl font-semibold text-center mb-2">
                {t("signUp")}
              </h2>
              <p className="text-xs text-gray-400 mb-5">
                {t("pleaseFillRegisterData")}
              </p>

              {currentStep === 1 && (
                <div className="flex flex-col w-full gap-4">
                  <h2 className="text-xl font-semibold text-center mb-4">{t("yourPhone")}</h2>
                  <div className="relative w-full">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 flex items-center gap-1 text-gray-500">
                      <img src={saudi_flag} alt="Saudi Flag" className="w-8 h-5 object-cover" />
                      <p>+966</p>
                    </div>
                    <input
                      type="text"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="966xxxxxxxx"
                      className="w-full pl-24 px-5 py-2 border rounded-full focus:outline-none"
                    />
                  </div>
                  {sendOtpErr && <p className="text-red-500 text-sm">{sendOtpErr}</p>}

                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={sendingOtp}
                    className="w-full bg-purble-2 text-white py-2 
                    rounded-full hover:bg-blue-600 transition flex
                    items-center justify-center"
                  >
                    {sendingOtp ? <LoaderMini color="bg-white" /> : t("sendOtp")}
                  </button>
                </div>
              )}

              {currentStep === 2 && (
                <div className="flex flex-col w-full gap-4">
                  <h2 className="text-xl font-semibold text-center mb-4">{t("enterOtp")}</h2>
                  <div className="flex justify-center gap-2" dir="ltr">
                    {otpDigits.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        maxLength="1"
                        placeholder="-"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        className="w-12 h-12 text-center border border-gray-300 rounded-lg text-xl focus:outline-none"
                      />
                    ))}
                  </div>

                  {verifyOtpErr && <p className="text-red-500 text-sm">{verifyOtpErr}</p>}

                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={verifyingOtp}
                    className="w-full bg-purble-2 text-white py-2 
                    rounded-full hover:bg-blue-600 transition flex
                    items-center justify-center"
                  >
                    {verifyingOtp || sendingOtp ? <LoaderMini color="bg-white" /> : t("verifyOtp")}
                  </button>

                  {/* Resend Button */}
                  {otpSent && (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      className="text-blue-800 underline underline-offset-2 text-sm mt-2"
                    >
                      {t("didntRecieve")} {t("resendOtp")}
                    </button>
                  )}

                  {/* OTP Timer Message */}
                  <p className="text-gray-500 text-xs text-center mt-2">
                    {timeLeft > 0 ? `${t("otpExpiresIn")} ${formatTime(timeLeft)}` : t("otpExpired")}
                  </p>
                </div>
              )}

              {currentStep === 3 && <form onSubmit={handleSubmit(onSubmit)} className="w-full mb-4">
                <div className="w-full flex flex-col lg:flex-row gap-2">
                  <div className="w-full flex items-center justify-center flex-col mb-5">
                    <label className="hidden lg:block text-sm w-full text-start font-medium text-gray-700 mb-2">
                      {t("name")} *
                    </label>
                    <input
                      {...register("name", {
                        required: t("requiredName"),
                      })}
                      placeholder={t("yourName")}
                      className="w-full px-5 py-2 border rounded-full focus:outline-none 
                flex items-center justify-center"
                    />
                    {errors.name && (
                      <p className="text-red-500 w-full text-start text-sm mt-1">
                        {errors.name.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-full flex flex-col lg:flex-row gap-2">
                  <div className="w-full flex items-center justify-center flex-col mb-5">
                    <label className="block text-sm w-full text-start font-medium text-gray-700 mb-2">
                      {t("email")} *
                    </label>
                    <input
                      type="email"
                      {...register("email", {
                        required: t("requiredEmail"),
                        pattern: {
                          value: /^[^@]+@[^@]+\.[^@]+$/,
                          message: t("invalidEmail"),
                        },
                      })}
                      placeholder={t("yourEmail")}
                      className="w-full px-5 py-2 border rounded-full focus:outline-none 
                flex items-center justify-center"
                    />
                    {errors.email && (
                      <p className="text-red-500 w-full text-start text-sm mt-1">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  {/* <div className="w-full flex items-center justify-center flex-col mb-5">
                    <label className="block text-sm w-full text-start font-medium text-gray-700 mb-2">
                      {t("phone")} *
                    </label>
                    <div className="relative w-full">
                      <div
                        className="absolute left-4 top-1/2 transform 
                  -translate-y-1/2 text-gray-500 text-sm flex items-center justify-center gap-1"
                      >
                        <img
                          src={saudi_flag}
                          alt=""
                          className="w-8 h-5 object-cover"
                        />
                        <p> +966 </p>
                      </div>
                      <input
                        {...register("phone", {
                          required: t("requiredPhone"),
                          pattern: {
                            value: /^\d{7,10}$/,
                            message: t("phoneValidation"),
                          },
                        })}
                        placeholder="966xxxxxxxxx"
                        className="w-full pl-24 px-5 py-2 border rounded-full focus:outline-none"
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-red-500 w-full text-start text-sm mt-1">
                        {errors.phone.message}
                      </p>
                    )}
                  </div> */}
                </div>

                <div className="w-full flex flex-col lg:flex-row gap-2">
                  <div className="w-full  group relative flex items-center justify-center flex-col mb-5">
                    <label className="block text-sm w-full text-start font-medium text-gray-700 mb-2">
                      {t("dateOfBirth")} *
                    </label>
                    <input
                      type="date"
                      {...register("date_of_birth", {
                        required: t("requiredBirthDate"),
                      })}
                      className="w-full px-5 py-2 border rounded-full focus:outline-none 
                flex justify-between bg-white text-black"
                    />
                    {errors.date_of_birth && (
                      <p className="text-red-500 w-full text-start text-sm mt-1">
                        {errors.date_of_birth.message}
                      </p>
                    )}
                  </div>

                  <div className="w-full flex items-center justify-center flex-col mb-5">
                    <label className="block text-sm w-full text-start font-medium text-gray-700 mb-2">
                      {t("gender")} *
                    </label>
                    <div className="w-full flex items-start justify-center flex-col rounded-full border ">
                      <select
                        {...register("gender", {
                          required: t("requiredGender"),
                        })}
                        className={`w-full px-5 py-2 lg:py-[9px] 
                          rounded-full focus:outline-none 
                flex items-center justify-center border-white bg-white
                ${isEng ? "border-r-[25px]" : "border-l-[25px]"}`}
                      >
                        <option className="w-80" value="">
                          {t("gender")}
                        </option>
                        <option className="w-80" value="male">
                          {t("male")}
                        </option>
                        <option className="w-80" value="female">
                          {t("female")}
                        </option>
                      </select>
                    </div>
                    {errors.gender && (
                      <p className="text-red-500 w-full text-start text-sm mt-1">
                        {errors.gender.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-full flex flex-col lg:flex-row gap-2">
                  <div className="relative w-full flex items-center justify-center flex-col mb-5">
                    <label className="block text-sm w-full text-start font-medium text-gray-700 mb-2">
                      {t("password")} *
                    </label>
                    <input
                      type={showPassword ? "text" : "password"}
                      {...register("password", {
                        required: t("requiredPassword"),
                        minLength: {
                          value: 6,
                          message: t("passwordMinLength"),
                        },
                      })}
                      placeholder={t("password")}
                      className="w-full px-5 py-2 border rounded-full focus:outline-none 
                flex items-center justify-center"
                    />
                    <button
                      type="button"
                      onClick={handleShowPassword}
                      className={`flex items-center justify-center absolute 
               top-9 bg-white text-[#402D7E]
              ${isEng ? "right-5" : "left-5"}`}
                    >
                      {showPassword ? (
                        <VscEyeClosed size={27} />
                      ) : (
                        <VscEye size={27} />
                      )}
                    </button>
                    {errors.password && (
                      <p className="text-red-500 w-full text-start text-sm mt-1">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                  <div className="relative w-full flex items-center justify-center flex-col mb-5">
                    <label className="block text-sm w-full text-start font-medium text-gray-700 mb-2">
                      {t("confirmPassword")} *
                    </label>
                    <input
                      type={showConfirmedPassword ? "text" : "password"}
                      {...register("confirmPassword", {
                        required: t("requiredConfirmPassword"),
                        validate: (value) =>
                          value === password || t("unmatchedPassword"),
                      })}
                      placeholder={t("confirmPassword")}
                      className="w-full px-5 py-2 border rounded-full focus:outline-none 
                flex items-center justify-center"
                    />
                    <button
                      type="button"
                      className={`flex items-center justify-center absolute 
               top-9 bg-white text-[#402D7E]
              ${isEng ? "right-5" : "left-5"}`}
                      onClick={handleShowConfirmedPassword}
                    >
                      {showConfirmedPassword ? (
                        <VscEyeClosed size={27} />
                      ) : (
                        <VscEye size={27} />
                      )}
                    </button>
                    {errors.confirmPassword && (
                      <p className="text-red-500 w-full text-start text-sm mt-1">
                        {errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-800 text-white py-2 rounded-full hover:bg-blue-600 
              transition disabled:bg-gray-400 flex items-center justify-center"
                  disabled={completingRegistration}
                >
                  {completingRegistration ? (
                    <div className="w-full flex items-center justify-center">
                      <LoaderMini color="bg-yellow-400" />
                    </div>
                  ) : (
                    t("signUp")
                  )}
                </button>
              </form>}
              {/* <div
                className="flex items-center justify-center w-full my-4 border-[0.5px]
          border-gray-300 relative"
              >
                <p
                  className="absolute text-xs bg-white z-10 text-gray-500 px-2
             -translate-y-"
                >
                  {t("orSignUpWithGoogle")}
                </p>
              </div>
              <div className="my-4 w-full">
                <button
                  className="w-full flex items-center justify-center gap-2 
                py-2 rounded-full border hover:bg-blue-200 transition"
                  onClick={() => alert("Google Sign-In Clicked!")}
                >
                  <div>
                    <FcGoogle />
                  </div>
                  <span>{t("signUpWithGoogle")}</span>
                </button>
              </div> */}
              <div className="flex items-center justify-center w-full gap-1 mt-4">
                <span>{t("alreadyHaveAccount")}</span>
                <button
                  type="button"
                  onClick={() => handlePopup("login")}
                  className="text-blue-800 underline underline-offset-2 hover:text-blue-500
              "
                >
                  {t("login")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default RegisterPopup;
